import Bowser from 'bowser';

import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { ApAuthenticationService, AuthenticationToken, RegistrationStage } from '@ap/browser-authenticator';
import { ApError, ApErrorService } from '@ap/error';
import {
    ApAuthenticatieFidoResource,
    BrowserRegistration,
    DeviceRegistrationStatus,
    DeviceRegistrationType,
    FidoAuthenticationRequest,
    FidoAuthenticationResponse
} from '@ap/interface-apic-authenticatie-fido';
import { SessiebeheerResource, Session, SessionStatus, UserType } from '@ap/interface-apic-sessiebeheer';
import { PageOfDevices, TokenbeheerResource } from '@ap/interface-apic-tokenbeheer';
import { PartyType, TokenregistratieResource } from '@ap/interface-apic-tokenregistratie';
import { ModalOptions, ModalSize } from '@ap/modal';
import { ApPersistenceService } from '@ap/persistence';
import { ApBrowserService } from '@ap/sign';
import { ErrorContext } from '@ap/tokens-browser';

import { BrowserSessionName } from './mijn-tokens-browser.constants';
import { BrowserTokensSession } from './mijn-tokens-browser.model';

/**
 * @module MijnTokensBrowserModule
 * @ngdoc service
 * @name MijnTokensBrowserService
 */
@Injectable({ providedIn: 'root' })
export class MijnTokensBrowserService {
    private device: string;
    private name: string;
    private pincode: string;
    private registrationId: string;
    private taskId: string;
    private keychainId: string;
    private readonly currentSession = '0';
    private readonly showNotification = new BehaviorSubject<boolean>(false);

    public constructor(
        private readonly apAuthenticationService: ApAuthenticationService,
        private readonly apAuthenticatieFidoResource: ApAuthenticatieFidoResource,
        private readonly apBrowserService: ApBrowserService,
        private readonly apErrorService: ApErrorService,
        private readonly persistenceService: ApPersistenceService,
        private readonly sessiebeheerResource: SessiebeheerResource,
        private readonly tokenbeheerResource: TokenbeheerResource,
        private readonly tokenregistratieResource: TokenregistratieResource
    ) {}

    public static getBrowserFamily(): string {
        return Bowser.getParser(window.navigator.userAgent).getBrowserName();
    }

    private static getOsFamily(): string {
        return Bowser.getParser(window.navigator.userAgent).getOSName();
    }

    public setDevice(device: string): void {
        this.device = device;
    }

    public getDevice(): string {
        return this.device;
    }

    public setName(name: string): void {
        this.name = name;
    }

    public getName(): string {
        return this.name;
    }

    public setPincode(pincode: string): void {
        this.pincode = pincode;
    }

    public getPincode(): string {
        return this.pincode;
    }

    public patchDevice(authenticationTaskId?: string): Observable<HttpResponse<void>> {
        return this.tokenbeheerResource.getDevices().pipe(
            switchMap((pageOfDevices: PageOfDevices) =>
                this.tokenregistratieResource.patchDevice(
                    pageOfDevices.devices[0].id,
                    {
                        user: {
                            type: PartyType.CUSTOMER
                        }
                    },
                    authenticationTaskId
                )
            )
        );
    }

    public removeFromBrowser(): void {
        this.apAuthenticationService
            .deleteToken(this.keychainId)
            .pipe(
                switchMap(() => this.resetState()),
                tap(() => this.persistenceService.clearSession(BrowserSessionName))
            )
            .subscribe();
    }

    public registerBrowser(): Observable<BrowserTokensSession> {
        const storage: BrowserTokensSession = {} as BrowserTokensSession;
        return this.doBrowserRegistration().pipe(
            tap((keychainId) => {
                storage.keychainId = keychainId;
                this.keychainId = keychainId;
            }),
            switchMap((keychainId: string) => this.doAnonymousLogin(keychainId)),
            map(() => storage),
            catchError((err) => {
                this.apErrorService.publish(new ApError(null, ErrorContext.DEFAULT));
                return of(err);
            })
        );
    }

    public getShowNotification(): Observable<boolean> {
        return this.showNotification;
    }

    public setShowNotification(newStatus: boolean): void {
        this.showNotification.next(newStatus);
    }

    public setBrowserTokenStage(keychainId: string, stage: RegistrationStage): Observable<void> {
        return this.apAuthenticationService.setStage(keychainId, stage);
    }

    public canContinueBrowserRegistration(): Observable<boolean> {
        return this.sessiebeheerResource.getSession(this.currentSession).pipe(
            switchMap((session: Session) => {
                if (session.user) {
                    return session.user.type.includes(UserType.ANONYMOUS) ? this.terminateUserSession() : of(false);
                }
                return of(true);
            }),
            map((isLoggedOut: boolean | void) => isLoggedOut !== false)
        );
    }

    public terminateUserSession(): Observable<void> {
        return this.sessiebeheerResource.patchSession(this.currentSession, { status: SessionStatus.TERMINATED });
    }

    public getModalOptions(): ModalOptions<any> {
        const modalOptions: ModalOptions<any> = new ModalOptions();
        modalOptions.cancelable = false;
        modalOptions.fullscreen = true;
        modalOptions.showHeader = false;
        modalOptions.size = ModalSize.LARGE;

        return modalOptions;
    }

    public getRegisteredTokens(): Observable<AuthenticationToken[]> {
        return this.apBrowserService.getTokens(true);
    }

    public getRegisteredNames(): Observable<string[]> {
        return this.getRegisteredTokens().pipe(map((result) => result.map((token) => token.username)));
    }

    public resetState(): Observable<void> {
        this.device = null;
        this.name = null;
        this.pincode = null;
        this.registrationId = null;
        this.keychainId = null;

        return this.sessiebeheerResource.patchSession('0', { status: SessionStatus.TERMINATED });
    }

    public browserIsIE(): boolean {
        return Bowser.getParser(window.navigator.userAgent).isBrowser(Bowser.BROWSER_MAP.ie, true);
    }

    public hasIncompatibleBrowser(): Observable<boolean> {
        return this.apAuthenticationService.isBrowserCompatible().pipe(map((isCompatible) => !isCompatible));
    }

    private getBrowserRegistration(): BrowserRegistration {
        return {
            os_family: MijnTokensBrowserService.getOsFamily(),
            browser_family: MijnTokensBrowserService.getBrowserFamily(),
            alias: this.getDevice(),
            type: DeviceRegistrationType.BROWSER
        };
    }

    private doBrowserRegistration(): Observable<string> {
        let keychainId: string;
        return this.apAuthenticatieFidoResource.postOneDeviceRegistration(this.getBrowserRegistration()).pipe(
            tap((id: string) => (this.registrationId = id)),
            switchMap(() => this.apBrowserService.registerToken(this.registrationId, this.device, this.pincode, this.name)),
            tap((id: string) => (keychainId = id)),
            switchMap(() =>
                this.apAuthenticatieFidoResource.patchOneDeviceRegistration(this.registrationId, {
                    status: DeviceRegistrationStatus.REGISTERED
                })
            ),
            map(() => keychainId)
        );
    }

    private doAnonymousLogin(keychainId: string): Observable<void> {
        const requestSession: Session = {
            authentication_level: 30,
            started_from_device_fingerprint: this.apAuthenticationService.getFingerprint()
        };

        return this.getTaskId(requestSession).pipe(
            tap((sessionTaskId: string) => {
                this.taskId = sessionTaskId;
            }),
            switchMap(() => this.apAuthenticatieFidoResource.getOneAuthenticationRequest(this.taskId)),
            switchMap((request: FidoAuthenticationRequest) =>
                this.apAuthenticationService.generateAuthenticationResponse(keychainId, request, this.pincode)
            ),
            switchMap((authenticationResponse: FidoAuthenticationResponse) =>
                this.apAuthenticatieFidoResource.putOneAuthenticationResponse(this.taskId, authenticationResponse)
            ),
            switchMap(() => this.sessiebeheerResource.postSession(requestSession, { authenticationTaskIdHeaderParam: this.taskId }))
        );
    }

    private getTaskId(requestSession: Session): Observable<string | void> {
        return this.sessiebeheerResource.postSession(requestSession).pipe(
            switchMap((response) => {
                if (typeof response === 'object') {
                    return of('X-Authentication-Task-Id');
                }
                return null;
            }),
            catchError((error: HttpErrorResponse) => of(error.headers ? error.headers.get('X-Authentication-Task-Id') : null))
        );
    }
}
